import { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify';
import { Bot, X, Send, ThumbsDown, ThumbsUp, Trash2, Loader2, Volume2, VolumeX } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  PromptInput,
  PromptInputBody,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputTools,
} from '@/components/ai-elements/prompt-input';
import { cn } from '@/lib/utils';
import { useHelpChat } from './useHelpChat';
import { VoiceOrb } from '@/components/ai-elements/voice-orb';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { useSpokenResponse } from '@/hooks/useSpokenResponse';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { useApiToken } from '@/auth/hooks';

// ─── Route → lesbarer Kontext ─────────────────────────────────────────────────

const PAGE_LABELS: { pattern: RegExp; label: string }[] = [
  { pattern: /\/dashboard/,            label: 'Sopho Dashboard' },
  { pattern: /\/documents/,            label: 'Sopho Data Viewer' },
  { pattern: /\/uploader/,             label: 'Sopho Uploader' },
  { pattern: /\/chat|\/talk-to-data/,  label: 'Sopho Chat' },
  { pattern: /\/graph/,                label: 'Wissensgraph-Editor' },
  { pattern: /\/connectors/,           label: 'Sopho Connector' },
  { pattern: /\/steward/,              label: 'Sopho Steward' },
  { pattern: /\/ontology|\/ontologie/, label: 'Ontologie-Verwaltung' },
  { pattern: /\/machines/,             label: 'Sopho Machine Models' },
  { pattern: /\/interview/,            label: 'Sopho Interviewer' },
  { pattern: /\/ruleset-analyzer/,     label: 'Sopho Ruleset' },
  { pattern: /\/rollout-kpi/,          label: 'Sopho Quality' },
  { pattern: /\/governance/,           label: 'Sopho Governance' },
  { pattern: /\/flows/,                label: 'Sopho Flows' },
  { pattern: /\/sopho-fuse/,           label: 'Sopho Fuse' },
  { pattern: /\/chunk-visualizer/,     label: 'Sopho Chunk Visualizer' },
  { pattern: /\/plugins\/marketplace/, label: 'Plugin-Marktplatz' },
  { pattern: /\/settings\/members/,    label: 'Mitglieder-Verwaltung' },
  { pattern: /\/workspaces/,           label: 'Workspace-Verwaltung' },
  { pattern: /\/domains/,              label: 'Domain-Verwaltung' },
  { pattern: /\/help/,                 label: 'Hilfe & Support' },
];

function getPageLabel(pathname: string): string {
  for (const { pattern, label } of PAGE_LABELS) {
    if (pattern.test(pathname)) return label;
  }
  return 'SophoGraph';
}

const CONTEXT_HINTS: Record<string, { placeholder: string; suggestions: string[] }> = {
  'Sopho Uploader': {
    placeholder: 'Frage zu Upload, Triage oder Vorschau…',
    suggestions: [
      'Wie funktioniert der Upload-Flow?',
      'Warum bleibt ein Dokument auf AwaitingConfirmation?',
      'Wann nutze ich Uploader statt Data Viewer?',
    ],
  },
  'Sopho Steward': {
    placeholder: 'Frage zu Review, Freigabe oder Report-Status…',
    suggestions: [
      'Was passiert beim Finalisieren eines Reports?',
      'Wer darf mit Sopho Steward arbeiten?',
      'Wann sollte ich Inhalte als veraltet markieren?',
    ],
  },
  default: {
    placeholder: 'Frage stellen… (Enter zum Senden)',
    suggestions: [
      'Was kann ich auf dieser Seite tun?',
      'Was ist der nächste sinnvolle Schritt?',
      'Welche Rolle brauche ich dafür?',
    ],
  },
};

// ─── Markdown renderer ────────────────────────────────────────────────────────

function escapeHtml(s: string) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function renderMarkdown(text: string): string {
  // 1. Code-Blöcke extrahieren und schützen (Platzhalter)
  const codeBlocks: string[] = [];
  let s = text.replace(/```([\w]*)\n?([\s\S]*?)```/g, (_m, _lang, code: string) => {
    const i = codeBlocks.length;
    codeBlocks.push(`<pre class="bg-muted/80 border border-border/40 rounded-md p-3 text-xs overflow-x-auto my-2 font-mono whitespace-pre-wrap leading-relaxed">${escapeHtml(code.trimEnd())}</pre>`);
    return `\x00CODE${i}\x00`;
  });

  // 2. Inline-Code
  s = s.replace(/`([^`]+)`/g, '<code class="bg-muted/80 border border-border/40 rounded px-1.5 py-0.5 font-mono text-[11px]">$1</code>');

  // 3. Zeilen splitten und Block-Elemente verarbeiten
  const lines = s.split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Überschriften
    const h3 = line.match(/^###\s+(.+)/);
    const h2 = line.match(/^##\s+(.+)/);
    const h1 = line.match(/^#\s+(.+)/);
    if (h1) { out.push(`<h1 class="text-sm font-bold mt-3 mb-1">${inlineMarkdown(h1[1])}</h1>`); i++; continue; }
    if (h2) { out.push(`<h2 class="text-xs font-bold mt-3 mb-1">${inlineMarkdown(h2[1])}</h2>`); i++; continue; }
    if (h3) { out.push(`<h3 class="text-xs font-semibold mt-2 mb-0.5">${inlineMarkdown(h3[1])}</h3>`); i++; continue; }

    // Aufzählung (- oder *)
    if (/^[-*]\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(`<li class="leading-relaxed">${inlineMarkdown(lines[i].replace(/^[-*]\s/, ''))}</li>`);
        i++;
      }
      out.push(`<ul class="list-disc list-outside ml-4 space-y-0.5 my-1.5">${items.join('')}</ul>`);
      continue;
    }

    // Nummerierte Liste
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(`<li class="leading-relaxed">${inlineMarkdown(lines[i].replace(/^\d+\.\s/, ''))}</li>`);
        i++;
      }
      out.push(`<ol class="list-decimal list-outside ml-4 space-y-0.5 my-1.5">${items.join('')}</ol>`);
      continue;
    }

    // Leerzeile → Absatz-Abstand
    if (line.trim() === '') {
      out.push('<div class="h-2"></div>');
      i++;
      continue;
    }

    // Normaler Text
    out.push(`<p class="leading-relaxed">${inlineMarkdown(line)}</p>`);
    i++;
  }

  // 4. Code-Blöcke wiederherstellen
  let result = out.join('');
  codeBlocks.forEach((block, idx) => {
    result = result.replace(`\x00CODE${idx}\x00`, block);
  });

  return result;
}

function inlineMarkdown(s: string): string {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code class="bg-muted/80 border border-border/40 rounded px-1.5 py-0.5 font-mono text-[11px]">$1</code>');
}

// 0113.01 (Audit 2026-05-12, Frontend-B-01): DOMPurify-Konfiguration für die
// HelpFab-Markdown-Ausgabe. Nur strukturelle Tags und Tailwind-Klassen erlaubt;
// alle Event-Handler, javascript:-URLs, <script>, <iframe>, <style> etc. werden
// gestrippt. Wird auf das fertige renderMarkdown()-Output angewandt.
const HELP_MARKDOWN_PURIFY_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    'p', 'br',
    'strong', 'em', 'code', 'pre',
    'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'span', 'div',
  ],
  ALLOWED_ATTR: ['class'],
  // Hard-disable jeglicher gefährlicher Tags & Attribute, auch wenn jemand
  // sie versehentlich der ALLOWED_TAGS-Liste hinzufügt.
  FORBID_TAGS: ['script', 'iframe', 'style', 'object', 'embed', 'form', 'input', 'a', 'img'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'href', 'src', 'srcset', 'style'],
  KEEP_CONTENT: true,
  RETURN_TRUSTED_TYPE: false,
};

export function sanitizeHelpMarkdown(rawMarkdown: string): string {
  // Mit RETURN_TRUSTED_TYPE: false ist das Return immer string; der
  // Union-Type von dompurify v3 enthält trotzdem TrustedHTML, deshalb
  // der explizite Two-Step-Cast zum Beruhigen des TS-strict-Modes.
  return DOMPurify.sanitize(renderMarkdown(rawMarkdown), HELP_MARKDOWN_PURIFY_CONFIG) as unknown as string;
}

// ─── Resize hook ──────────────────────────────────────────────────────────────

const MIN_W = 300;
const MIN_H = 360;
const MAX_W = 720;
const MAX_H = 820;

function useResize(initialW: number, initialH: number) {
  const [size, setSize] = useState({ w: initialW, h: initialH });
  const startRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);

  function makeHandler(axis: 'x' | 'y') {
    return function onMouseDown(e: React.MouseEvent) {
      e.preventDefault();
      startRef.current = { x: e.clientX, y: e.clientY, w: size.w, h: size.h };

      function onMove(ev: MouseEvent) {
        if (!startRef.current) return;
        if (axis === 'x') {
          const dx = startRef.current.x - ev.clientX;
          setSize((s) => ({ ...s, w: Math.min(MAX_W, Math.max(MIN_W, startRef.current!.w + dx)) }));
        } else {
          const dy = startRef.current.y - ev.clientY;
          setSize((s) => ({ ...s, h: Math.min(MAX_H, Math.max(MIN_H, startRef.current!.h + dy)) }));
        }
      }

      function onUp() {
        startRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      }

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };
  }

  return { size, onLeftEdgeMouseDown: makeHandler('x'), onTopEdgeMouseDown: makeHandler('y') };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function HelpFab() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const location = useLocation();
  const pageLabel = getPageLabel(location.pathname);
  const contextHints = CONTEXT_HINTS[pageLabel] ?? CONTEXT_HINTS.default;
  const { getAccessToken } = useApiToken();
  const { messages, isStreaming, sendMessage, submitFeedback, clearMessages } = useHelpChat(pageLabel, getAccessToken);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef('');
  const { size, onLeftEdgeMouseDown, onTopEdgeMouseDown } = useResize(360, 500);

  // ── Voice input (Task 10) ──────────────────────────────────────────────
  const workspaceId = useWorkspaceStore((s) => s.activeWorkspace?.id ?? '');

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  const handleTranscriptChange = useCallback(
    (text: string) => {
      if (text) {
        setInput(text);
      }
    },
    [],
  );

  const voice = useVoiceInput({
    featureContext: 'help',
    workspaceId,
    onTranscriptChange: handleTranscriptChange,
    getCurrentText: () => inputRef.current,
    pageContext: pageLabel,
    getToken: getAccessToken,
  });

  const {
    enabled: spokenResponseEnabled,
    available: spokenResponseAvailable,
    speakProgressively,
    toggleEnabled: toggleSpokenResponse,
  } = useSpokenResponse({ getToken: getAccessToken });

  useEffect(() => {
    if (!spokenResponseEnabled) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== 'assistant' || !lastMessage.content.trim()) {
      return;
    }

    speakProgressively(lastMessage.id, lastMessage.content, !isStreaming);
  }, [isStreaming, messages, speakProgressively, spokenResponseEnabled]);

  const isVoice = voice.status !== 'idle';

  // Wenn die Seite wechselt und der Chat leer ist, muss nichts zurückgesetzt werden.
  // Hat der Nutzer schon Nachrichten, behalten wir sie — der neue page_context
  // gilt ab der nächsten Nachricht.

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleSend() {
    if (!input.trim() || isStreaming) return;
    const text = input;
    if (isVoice) {
      voice.stop();
    }
    setInput('');
    await sendMessage(text);
  }

  return (
    <div className="fixed bottom-[52px] right-4 z-50 flex flex-col items-end gap-2">
      {open && (
        <>
          {/* Chat panel */}
          <div
            style={{ width: size.w, height: size.h }}
            className="relative flex flex-col rounded-xl border border-border/60 bg-background shadow-2xl shadow-black/20 overflow-hidden"
          >
            {/* Top edge — resize height */}
            <div
              onMouseDown={onTopEdgeMouseDown}
              className="absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize z-10 hover:bg-primary/20 transition-colors rounded-t-xl"
              title="Höhe ändern"
            />
            {/* Left edge — resize width */}
            <div
              onMouseDown={onLeftEdgeMouseDown}
              className="absolute top-0 left-0 bottom-0 w-1.5 cursor-ew-resize z-10 hover:bg-primary/20 transition-colors rounded-l-xl"
              title="Breite ändern"
            />

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/40 bg-muted/30 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Bot className="h-4 w-4 text-sopho-entity shrink-0" />
                <div className="min-w-0">
                  <span className="text-sm font-semibold">SophoGraph Assistent</span>
                  <p className="text-[10px] text-muted-foreground truncate">{pageLabel}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {messages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={clearMessages}
                    title="Chat leeren"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center gap-3 text-muted-foreground">
                  <Bot className="h-8 w-8 opacity-30" />
                  <div>
                    <p className="text-sm font-medium">Wie kann ich dir helfen?</p>
                    <p className="text-xs mt-1 opacity-70">
                      Du bist auf <span className="font-medium text-foreground/60">{pageLabel}</span>.
                    </p>
                  </div>
                  <div className="flex max-w-[92%] flex-wrap justify-center gap-2 pt-1">
                    {contextHints.suggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => setInput(suggestion)}
                        className="rounded-full border border-border/50 bg-background/70 px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:border-primary/30 hover:text-foreground"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((msg, i) => (
                <div
                  key={msg.id}
                  className={cn(
                    'flex',
                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[88%] rounded-xl px-3.5 py-2.5 text-xs leading-relaxed',
                      msg.role === 'user'
                        ? 'bg-sopho-entity text-white rounded-br-sm'
                        : 'bg-muted/70 text-foreground border border-border/30 rounded-bl-sm'
                    )}
                  >
                    {msg.role === 'assistant' ? (
                      msg.content ? (
                        <span
                          className="[&_ol]:my-1 [&_ul]:my-1 [&_li]:leading-snug [&_strong]:font-semibold [&_pre]:text-xs"
                          dangerouslySetInnerHTML={{ __html: sanitizeHelpMarkdown(msg.content) }}
                        />
                      ) : isStreaming && i === messages.length - 1 ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin opacity-60" />
                      ) : null
                    ) : (
                      msg.content
                    )}
                    {msg.role === 'assistant' && msg.langfuseTraceId && msg.content && (
                      <div className="mt-2 flex items-center gap-1">
                        <button
                          type="button"
                          aria-label="Hilfreiche Antwort bewerten"
                          disabled={msg.feedbackState === 'submitting' || msg.feedbackState === 'submitted'}
                          onClick={() => void submitFeedback(msg.id, 'up')}
                          className={cn(
                            'inline-flex items-center justify-center rounded-full border p-1 transition-colors',
                            msg.feedbackValue === 'up'
                              ? 'border-green-500/40 bg-green-500/10 text-green-700 dark:text-green-300'
                              : 'border-border/50 text-muted-foreground hover:border-primary/30 hover:bg-background/60 hover:text-foreground',
                          )}
                        >
                          <ThumbsUp className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          aria-label="Nicht hilfreiche Antwort bewerten"
                          disabled={msg.feedbackState === 'submitting' || msg.feedbackState === 'submitted'}
                          onClick={() => void submitFeedback(msg.id, 'down')}
                          className={cn(
                            'inline-flex items-center justify-center rounded-full border p-1 transition-colors',
                            msg.feedbackValue === 'down'
                              ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                              : 'border-border/50 text-muted-foreground hover:border-primary/30 hover:bg-background/60 hover:text-foreground',
                          )}
                        >
                          <ThumbsDown className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="shrink-0 border-t border-border/40 p-3 bg-background">
              <PromptInput
                onSubmit={() => { if (input.trim() && !isStreaming) void handleSend(); }}
                className={cn(
                  'w-full transition-colors',
                  isVoice && voice.status === 'listening' && 'ring-1 ring-voice-listening/40',
                  isVoice && voice.status === 'thinking' && 'ring-1 ring-voice-thinking/40',
                  isVoice && voice.status === 'speaking' && 'ring-1 ring-voice-speaking/40',
                )}
              >
                <PromptInputBody>
                  <div>
                    <PromptInputTextarea
                      value={input}
                      onChange={(e) => setInput(e.currentTarget.value)}
                      placeholder={contextHints.placeholder}
                      disabled={isStreaming}
                      className="text-sm"
                    />
                  </div>
                </PromptInputBody>

                <PromptInputFooter>
                  <PromptInputTools>
                    {/* VoiceOrb mic button */}
                    {workspaceId && voice.isSupported && (
                      <VoiceOrb
                        variant="inline"
                        showPanel={false}
                        status={voice.status}
                        isMuted={voice.isMuted}
                        liveTranscript={voice.transcript}
                        onStart={() => void voice.start()}
                        onStop={voice.stop}
                      />
                    )}
                    {spokenResponseAvailable && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={toggleSpokenResponse}
                        aria-label={spokenResponseEnabled ? 'Hören deaktivieren' : 'Hören aktivieren'}
                        title={spokenResponseEnabled ? 'Hören aktiviert: Antworten werden vorgelesen' : 'Hören aktivieren'}
                        className={cn(spokenResponseEnabled ? 'bg-primary/10 text-primary' : 'text-muted-foreground')}
                      >
                        {spokenResponseEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                      </Button>
                    )}
                    {workspaceId && !voice.isSupported && voice.unsupportedReason && (
                      <span className="max-w-44 text-[10px] leading-relaxed text-muted-foreground">
                        {voice.unsupportedReason}
                      </span>
                    )}
                  </PromptInputTools>

                  <Button
                    size="icon"
                    className="h-8 w-8 shrink-0 bg-sopho-entity hover:bg-sopho-entity/80"
                    onClick={() => void handleSend()}
                    disabled={!input.trim() || isStreaming}
                  >
                    {isStreaming ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                </PromptInputFooter>
              </PromptInput>
            </div>
          </div>
        </>
      )}

      {/* FAB button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'h-11 w-11 rounded-full flex items-center justify-center shadow-lg transition-all',
          'bg-gradient-to-br from-[#6366f1] to-[#8b5cf6]',
          'hover:scale-105 active:scale-95',
          open && 'rotate-12'
        )}
        aria-label="KI-Hilfe öffnen"
        title="KI-Hilfe öffnen"
      >
        <Bot className="h-5 w-5 text-white" />
      </button>
    </div>
  );
}
